/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import PropTypes from 'prop-types';

// @mui material components
import { Grid, Tabs, Tab, Box, Card } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React example components
import DefaultCounterCard from "examples/Cards/CounterCards/DefaultCounterCard";

//Backend access
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Route, Link, Routes, useLocation, useNavigate, useMatch } from 'react-router-dom';

import { calculateBattery } from "helper/device";
import DeviceStats from "nextus/Charts/DeviceStats";
import Weather from "nextus/Weather";
import LiveView from "nextus/LiveView";

import { useAuth0 } from "@auth0/auth0-react";
import ConnectivityStatisticCard from "nextus/Cards/ConnectivityStatisticCard";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import moment from "moment";
import DeviceSettingsForm from "nextus/Forms/DeviceSettings";
import EditDeviceMap from "nextus/Maps/EditDeviceMap";

import bksAPI from "api/bks";
import groupAPI from "api/groups";
import userAPI from "api/user";
import sitesAPI from "api/sites";

import DeviceEvents from "nextus/Charts/DeviceEvents";

import { processDeviceData } from "helper/device";
import { processGroupData } from "helper/group";
import DeviceMap from "nextus/Maps/DeviceMap";
import DeviceReportingForm from "nextus/Forms/DeviceReportingForm";
import LiveViewReportingForm from "nextus/Forms/LiveViewReportingForm";
import CameraFeed from "nextus/CameraFeed";
import { getUserAccessLevel } from "helper/user";
import LiveviewTimeline from "nextus/Timelines/LiveviewTimeline";
import CameraTimeline from "nextus/Timelines/CameraTimeline";
import { useSoftUIController } from "context";

const CameraUnits = ['1003', '1004', '1005', '1006', '1007', 'Mercury Carpark'];

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ height: 'calc(100% - 35px)' }}
    >
      {value === index && <Box sx={{ p: { xxs: 1, xs: 3 }, pb: 0, height: 'calc(100% - 24px)' }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function wrapper(content, deviceStyle) {
  return (
    <SoftBox pt={{ xxs: 0, xs: 3 }} pb={{ xxs: 2, xs: 3 }} height={{ xxs: 'calc(100% - 16px)', xs: 'calc(100% - 48px)' }}
      sx={({ palette: { white }, boxShadows: { navbarBoxShadow }, borders: { borderRadius }, functions: { rgba, pxToRem } }) => ({
        boxShadow: navbarBoxShadow,
        backdropFilter: `saturate(200%) blur(${pxToRem(4)})`,
        borderRadius: borderRadius.xl,

        overflowY: 'auto',
        overflowX: 'hidden',

        '::-webkit-scrollbar': {
          backgroundColor: 'transparent',
          width: '8px',
        },
        '::-webkit-scrollbar-track': { /* background of the scrollbar except button or resizer */
          backgroundColor: 'transparent',
        },
        '::-webkit-scrollbar-thumb': { /* scrollbar itself */
          backgroundColor: '#babac0',
          borderRadius: '6px',
          border: `1px solid ${rgba(white.main, 0.8)}`,
        },
        '::-webkit-scrollbar-button': { /* set button(top and bottom of the scrollbar) */
          display: 'none',
        },
      })}
    >
      {content}
    </SoftBox >
  )
}

function DeviceView({ map, navTitle, setNavTitle, sidebarState, setSidebarState }) {
  const navigate = useNavigate();
  const { uuid } = useMatch('devices/:uuid').params;

  const [controller, dispatch] = useSoftUIController();
  const { deviceStyle } = controller;

  const [tabValue, setTabValue] = useState(0);
  const [imageValue, setImageValue] = useState(0);
  const [deviceReportInputs, setDeviceReportInputs] = useState({ start_date: moment().subtract(7, 'days'), start_time: moment().subtract(7, 'days'), end_time: moment().subtract(6, 'days'), end_time: moment().subtract(6, 'days') })
  const [liveviewReportInputs, setLiveviewReportInputs] = useState({ start_date: moment().subtract(1, 'days'), start_time: moment().subtract(1, 'days') })

  const handleTabClick = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleImageTabClick = (event: React.SyntheticEvent, newValue: number) => {
    setImageValue(newValue);
  };

  groupAPI.loadToken(useAuth0());
  bksAPI.loadToken(useAuth0());
  userAPI.loadToken(useAuth0());
  sitesAPI.loadToken(useAuth0());

  const { user } = useAuth0();

  const device = useQuery({
    queryKey: ['getDevice', uuid],
    queryFn: async () => {
      return await bksAPI.getDevice(uuid);
    },
    staleTime: 20 * 1000
  });

  const sites = useQuery({
    queryKey: ['getAllSites'],
    queryFn: async () => {
      return await sitesAPI.getAllSites();
    }
  });

  const groups = useQuery({
    queryKey: ['getAllGroups'],
    queryFn: async () => {
      return await groupAPI.getAllGroups();
    }
  });

  const group = useQuery({
    queryKey: ['getGroupWithDeviceUUID', uuid],
    queryFn: async () => {
      return await groupAPI.getGroupWithDeviceUUID(uuid);
    }
  });

  const selected_user = useQuery({
    queryKey: ['getUserByEmail', user.email.toLowerCase()],
    queryFn: async () => {
      return await userAPI.getUserByEmail(user.email.toLowerCase());
    }
  });

  useEffect(() => {
    setSidebarState('sidenav');
  }, [sidebarState]);

  if (groups.isLoading || group.isLoading || device.isLoading || sites.isLoading || selected_user.isLoading) {
    return wrapper('Loading...', deviceStyle);
  }

  if (groups.error || group.error || device.error || sites.error || selected_user.error) {
    if (sites.error) {
      var error = sites.error;
    }
    else if (groups.error) {
      var error = groups.error;
    }
    else if (group.error) {
      var error = group.error;
    }
    else if (device.error) {
      var error = device.error;
    }
    else if (selected_user.error) {
      var error = selected_user.error;
    }
    return wrapper("An error has occurred: " + error.message, deviceStyle);
  }

  if (!device.data || !sites.data || !selected_user.data) {
    return wrapper('Loading...', deviceStyle);
  }

  var basic_groups_data = groups.data.map(function (group) {
    var ret = processGroupData(group, null, getUserAccessLevel(selected_user.data.user.user_type, 'technician'));
    return ret;
  }).filter((row) => { return typeof row.parent_id == 'undefined' });
  var group_data = group.data ? processGroupData(group.data, [device.data], getUserAccessLevel(selected_user.data.user.user_type, 'technician')) : null;
  var device_data = processDeviceData(device.data, group_data.type);

  const camera_feed_tab = (
    <CameraFeed uuid={uuid} />
    // <CameraTimeline uuid={uuid} />
  );

  const events_tab = (
    <Grid container spacing={{ xxs: 1, xs: 3 }} sx={{ height: 'calc(100% + 24px)' }}>
      <Grid item xxs={12}>
        <Grid>
          <DeviceEvents title="Events Chart" height="600px" uuid={uuid} />
        </Grid>
      </Grid>
    </Grid>
  );

  const device_settings_tab = (
    <SoftBox sx={{ height: '100%' }}>
      <Grid container spacing={3} sx={{ height: 'calc(100% + 24px)' }}>
        <Grid item xs={12} lg={12}>
          <DeviceSettingsForm selected_device={device_data} sites={sites.data} selected_group={group_data} selected_subgroup={group_data ? group_data.subgroups.filter((subgroup) => { return subgroup.uuids.indexOf(uuid) > -1 })[0] : {}} groups={basic_groups_data} />
        </Grid>
        {/* <Grid item xs={12} lg={3}>
          <Card sx={{ height: '200px' }} onClick={(e) => { e.stopPropagation() }}>
            <EditDeviceMap group={group_data} selected_device={device_data} padding={30} />
          </Card>
        </Grid> */}
      </Grid>
    </SoftBox>
  );

  return (
    wrapper((
      <SoftBox mb={2} sx={{ overflow: "visible", backgroundColor: 'transparent', height: '100%' }}>
        <Grid container>
          <Grid item xxs={12} sm={12} md={12} lg={12} xl={12}>
            <SoftBox mb={deviceStyle == 'mobile' ? 1 : 0}>
              <Tabs value={tabValue} onChange={handleTabClick} aria-label="DeviceView Tabs"
                allowScrollButtonsMobile
                variant="scrollable"
                scrollButtons
                sx={{
                  [`& .${tabsClasses.scrollButtons}`]: {
                    '&.Mui-disabled': { opacity: 0.3 },
                  },
                }}
              >
                <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Overview" {...a11yProps(0)} />
                <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Generate Report" {...a11yProps(1)} />
                <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Statistics" {...a11yProps(2)} />
                {getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (
                  <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Events" {...a11yProps(3)} />
                )}

                {(getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (CameraUnits.indexOf(device.data.name) > -1 || group_data.type == 'camera')) && (
                  <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Camera Feed" {...a11yProps(4)} />
                )}
                {(!getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (CameraUnits.indexOf(device.data.name) > -1 || group_data.type == 'camera')) && (
                  <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Camera Feed" {...a11yProps(3)} />
                )}

                {(getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (CameraUnits.indexOf(device.data.name) > -1 || group_data.type == 'camera')) && (
                  <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Settings" {...a11yProps(5)} />
                )}
                {(getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (CameraUnits.indexOf(device.data.name) < 0 && group_data.type != 'camera')) && (
                  <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Settings" {...a11yProps(4)} />
                )}
              </Tabs>
            </SoftBox>
          </Grid>
        </Grid>
        <CustomTabPanel value={tabValue} index={0}>
          <Grid container spacing={3} sx={{ height: 'calc(100% + 24px)' }}>
            <Grid item xxs={12} x3l={7} pb={3}>
              {/* <LiveView uuid={uuid} /> */}
              <LiveviewTimeline uuid={uuid} />
            </Grid>
            <Grid item xxs={12} x3l={5} pb={{ xxs: 1, xs: 3 }}>
              <Grid container spacing={3}>
                <Grid item xxs={12}>
                  <Grid container spacing={1}>
                    <Grid item xxs={12}>
                      <ConnectivityStatisticCard title={{ text: device_data.active ? `Last Updated: ${device_data.last_updated}` : `Inactive: ${device_data.last_updated}`, fontWeight: 'bold' }} bgColor={device_data.active ? device_data.connectivity == 'online' ? 'success' : 'error' : 'secondary'} icon={{ component: <PowerSettingsNewIcon />, color: "dark" }} />
                    </Grid>
                    <Grid item xxs={12} x4l={6}>
                      <ConnectivityStatisticCard variant='h6' title={{ text: `Serial No.: ${device_data.serial_number}`, fontWeight: 'bold' }} />
                    </Grid>
                    <Grid item xxs={12} x4l={6}>
                      <ConnectivityStatisticCard variant='h6' title={{ text: typeof device_data.site_id == 'undefined' ? `Site ID: Not Set` : `Site ID: ${device_data.site_id}`, fontWeight: 'bold' }} />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xxs={12}>
                  <Grid container spacing={1}>
                    <Grid item xxs={12} sm={6}>
                      <DefaultCounterCard
                        count={device_data.temperature}
                        suffix={<>&deg;C</>}
                        title="Temperature"
                        description=""
                      />
                    </Grid>
                    <Grid item xxs={12} sm={6}>
                      <DefaultCounterCard
                        count={device_data.humidity}
                        suffix="%"
                        title="Humidity"
                        description=""
                      />
                    </Grid>
                    <Grid item xxs={12} sm={6}>
                      <DefaultCounterCard
                        count={device_data.battery}
                        suffix="%"
                        title="Battery"
                        description=""
                      />
                    </Grid>
                    <Grid item xxs={12} sm={6}>
                      <DefaultCounterCard
                        count={device_data.rssi}
                        suffix="-dB"
                        title="Signal Strength"
                        description=""
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xxs={12}>
                  <Grid container spacing={1}>
                    {group_data != null && (
                      <Grid item xxs={12}>
                        <Card sx={{ height: '430px' }} onClick={(e) => { e.stopPropagation() }}>
                          <DeviceMap group={group_data} user_role={selected_user.data.user.user_type} setNavTitle={setNavTitle} selected_device={device_data} padding={50} />
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <Grid container spacing={{ xxs: 1, xs: 3 }} sx={{ height: 'calc(100% + 24px)' }}>
            <Grid item xxs={12} xxl={6}>
              <DeviceReportingForm uuid={uuid} reportInputs={deviceReportInputs} setReportInputs={setDeviceReportInputs} />
            </Grid>
            <Grid item xxs={12} xxl={6}>
              <LiveViewReportingForm uuid={uuid} reportInputs={liveviewReportInputs} setReportInputs={setLiveviewReportInputs} />
            </Grid>
          </Grid>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}>
          <Grid container spacing={{ xxs: 1, xs: 3 }} sx={{ height: 'calc(100% + 24px)' }}>
            <Grid item xxs={12}>
              <DeviceStats title="Stats Chart" height="400px" uuid={uuid} />
            </Grid>
          </Grid>
        </CustomTabPanel>
        {getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (
          <CustomTabPanel value={tabValue} index={3}>
            {events_tab}
          </CustomTabPanel>
        )}

        {(getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (CameraUnits.indexOf(device.data.name) > -1 || group_data.type == 'camera')) && (
          <CustomTabPanel value={tabValue} index={4}>
            {camera_feed_tab}
          </CustomTabPanel>
        )}
        {(!getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (CameraUnits.indexOf(device.data.name) > -1 || group_data.type == 'camera')) && (
          <CustomTabPanel value={tabValue} index={3}>
            {camera_feed_tab}
          </CustomTabPanel>
        )}

        {(getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (CameraUnits.indexOf(device.data.name) > -1 || group_data.type == 'camera')) && (
          <CustomTabPanel value={tabValue} index={5}>
            {device_settings_tab}
          </CustomTabPanel>
        )}
        {(getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (CameraUnits.indexOf(device.data.name) < 0 && group_data.type != 'camera')) && (
          <CustomTabPanel value={tabValue} index={4}>
            {device_settings_tab}
          </CustomTabPanel>
        )}
      </SoftBox>
    ), deviceStyle));
}

DeviceView.propTypes = {
  map: PropTypes.object,
  navTitle: PropTypes.array,
  setNavTitle: PropTypes.func,
  sidebarState: PropTypes.string,
  setSidebarState: PropTypes.func,
}

export default DeviceView;
